import { action, computed, extendObservable } from 'mobx';
import FetchService from '../../../core/services/FetchService';
import FormService from '../../../core/services/FormService';
import { pickBy, startCase, every } from 'lodash';
import NotificationService from '../../../core/services/NotificationService';
import ConfirmationDialogService from '../../../core/services/ConfirmationDialogService';

class DraftItemListService {
  fetch = new FetchService();
  submit = new FetchService();
  form = new FormService();

  constructor() {
    extendObservable(this, {
      items: [],
      type: '',
      page: {
        current: 1,
        total: 0,
        pageSize: 30,
      },
      selectedItems: [],
      enablePublishEntireUnit: computed(() => {
        return !!(
          this.form.getValue('unit') &&
          this.items.length &&
          every(this.items, (item) => item.unit && item.unit.id === this.form.getValue('unit'))
        );
      }),
      enablePublishEntireMastery: computed(() => {
        return !!(
          this.form.getValue('masteryTest') &&
          this.items.length &&
          every(this.items, (item) => item.masteryTest && item.masteryTest.id === this.form.getValue('masteryTest'))
        );
      }),
    });
    this.form.setInitialValues({});
  }

  init = action((type) => {
    this.type = type;
    this.items = [];
    this.page = {
      current: 1,
      total: 0,
      pageSize: 30,
    };
  });

  load = action(() => {
    this.fetch
      .fetch({
        url: `/draft-items?type=${this.type}`,
        query: {
          page: this.page.current,
          query: pickBy(this.form.getValues(), (x) => !!x),
        },
      })
      .then(() => {
        if (this.fetch.data) {
          this.page.total = this.fetch.data.total;
          if (this.page.total && !this.fetch.data.data.length) {
            this.handleTableChange({ current: 1 });
          } else {
            this.selectedItems = [];
            this.items = this.fetch.data.data || [];
          }
        }
      });
  });

  handleSelect = action((selectedRows) => {
    this.selectedItems = selectedRows;
  });

  handleTableChange = action((params) => {
    this.page.current = params.current;
    this.load();
  });

  handleChangeAssetStatus = action((id, assetType) => {
    this.submit
      .fetch({
        method: 'patch',
        url: `/draft-items/${id}/${assetType}/start`,
      })
      .then(() => {
        if (this.submit.data) {
          const assetToProp = {
            audio: 'audioAssetsStatus',
            image: 'imageAssetsStatus',
            video: 'videoAssetsStatus',
          };

          this.items = this.items.map((item) => {
            if (item.id === id) {
              return {
                ...item,
                [assetToProp[assetType]]: this.submit.data[assetToProp[assetType]],
              };
            }
            return item;
          });
        }
        if (this.submit.error) {
          NotificationService.addNotification('Error to change status', 'error');
        }
      });
  });

  handleRemove = action((draftItem) => {
    const message = {
      UNIT: `Would you like to delete the unit draft item with data "Order: ${
        draftItem.associativeItem?.order
      }, Group: ${['', 'Default', 'First review', 'Second review'][draftItem.associativeItem?.group]}, Text: ${
        draftItem.text
      }, Justification: ${draftItem.draftCreationReason}"? This action cannot be undone.`,
      MASTERY_TEST: `Would you like to delete the mastery test draft item with data "Order: ${draftItem.associativeItem?.order}, Text: ${draftItem.text}, Justification: ${draftItem.draftCreationReason}"? This action cannot be undone.`,
      PLACEMENT: `Would you like to delete the placement test draft item with data "Level: ${draftItem.level?.level}, Grammar: ${draftItem.grammar?.name}, Text: ${draftItem.text}, Justification: ${draftItem.draftCreationReason}"? This action cannot be undone.`,
      CERTIFICATION_TEST: `Would you like to delete the certification test draft item with data "Ability: ${startCase(
        draftItem.certificationTestCourseAbility?.ability.toLowerCase()
      )}, Grammar: ${draftItem.grammar?.name}, Text: ${draftItem?.text}, Justification: ${
        draftItem.draftCreationReason
      }"? This action cannot be undone.`,
    }[draftItem.scope];

    ConfirmationDialogService.show('Delete Draft Item', message, () => {
      this.submit
        .fetch({
          method: 'delete',
          url: {
            UNIT: `/units/${draftItem.unit?.id}/items/${draftItem.id}`,
            MASTERY_TEST: `/mastery-tests/${draftItem.masteryTest?.id}/items/${draftItem.id}`,
            PLACEMENT: `/grammar-placement-test-levels/${draftItem.grammarPlacementTestLevel?.id}/items/${draftItem.id}`,
            CERTIFICATION_TEST: `/certification-test-course-ability/${draftItem.certificationTestCourseAbility?.id}/items/${draftItem.id}`,
          }[draftItem.scope],
        })
        .then(() => {
          if (this.submit.data) {
            NotificationService.addNotification('Draft items successfully deleted', 'success');
            this.load();
          }
          if (this.submit.error) {
            NotificationService.addNotification(this.submit.error || 'Error to delete draft', 'error');
          }
        });
    });
  });

  handlePublish = action((draftItem) => {
    const message = {
      UNIT: `Do you really want to publish the unit draft item with data "Order: ${
        draftItem.associativeItem?.order
      }, Group: ${['', 'Default', 'First review', 'Second review'][draftItem.associativeItem?.group]}, Text: ${
        draftItem?.text
      }"?`,
      MASTERY_TEST: `Do you really want to publish the mastery test draft item with data "Order: ${draftItem.associativeItem?.order}, Text: ${draftItem?.text}"?`,
      PLACEMENT: `Do you really want to publish the placement test draft item with data "Level: ${draftItem.level?.level}, Grammar: ${draftItem.grammar?.name}, Text: ${draftItem?.text}"?`,
      CERTIFICATION_TEST: `Do you really want to publish the certification test draft item with data "Ability: ${startCase(
        draftItem.certificationTestCourseAbility?.ability.toLowerCase()
      )}, Grammar: ${draftItem.grammar?.name}, Text: ${draftItem?.text}"?`,
    }[draftItem.scope];

    ConfirmationDialogService.show('Publish Draft Item', message, () => {
      this.submit
        .fetch({
          method: 'patch',
          url: {
            UNIT: `/units/${draftItem.unit?.id}/items/${draftItem.id}/publish`,
            MASTERY_TEST: `/mastery-tests/${draftItem.masteryTest?.id}/items/${draftItem.id}/publish`,
            PLACEMENT: `/grammar-placement-test-levels/${draftItem.grammarPlacementTestLevel?.id}/items/${draftItem.id}/publish`,
            CERTIFICATION_TEST: `/certification-test-course-ability/${draftItem.certificationTestCourseAbility?.id}/items/${draftItem.id}/publish`,
          }[draftItem.scope],
        })
        .then(() => {
          if (this.submit.data) {
            NotificationService.addNotification('Draft item successfully published', 'success');
            this.load();
          }
          if (this.submit.error) {
            NotificationService.addNotification(this.submit.error || `Error to publish draft item`, 'error');
          }
        });
    });
  });
}

const draftItemListService = new DraftItemListService();

export default draftItemListService;
